import { Document, Image, Page, Text, View } from '@react-pdf/renderer';
import React from 'react';
import osvielLogo from '../../../assets/img/logos/osviel.jpg';
import superLogo from '../../../assets/img/logos/supersociedades.jpg';
import {
    BENEFICIARIO_FINAL_INTRO,
    CONDICIONES_LEGALES,
    CONSTANCIA,
    ORIGEN_FONDOS,
    PEP_APROBACION,
    PEP_DECLARACION,
    PEP_DEFINICION,
    PIE_PAGINA,
    RESULTADO_LISTAS
} from './legalText';
import styles, { COLORS } from './styles';

const FILAS_ANTICIPOS = 8;
const FILAS_BENEFICIARIO = 6;

// Campo con etiqueta en cursiva y el valor sobre una línea de puntos, como en el
// formato impreso. Sin valor queda la línea en blanco para diligenciar a mano.
const Field = ({ label, value, style, lineStyle }) => (
    <View style={[styles.fieldRow, style]}>
        {label ? <Text style={styles.label}>{label}</Text> : null}
        <View style={[styles.fill, { flex: 1 }, lineStyle]}>
            <Text style={styles.value}>{value || ' '}</Text>
        </View>
    </View>
);

const Row = ({ children, style }) => (
    <View style={[{ flexDirection: 'row' }, style]}>{children}</View>
);

const Header = () => (
    <View style={styles.header} fixed>
        <View style={styles.headerLeft}>
            <Image style={styles.superLogo} src={superLogo} />
            <Text style={styles.codeTag}>F – CR - 03</Text>
        </View>
        <View style={styles.headerCenter}>
            <Image style={styles.osvielLogo} src={osvielLogo} />
            <Text style={styles.tagline}>Su Operadora de Libranza / NIT 900.344.946-3</Text>
        </View>
        <View style={styles.headerRight}>
            <View style={{ borderTopWidth: 0.7, borderColor: '#000000', paddingTop: 2 }}>
                <Text style={{ fontFamily: 'Helvetica-BoldOblique', fontSize: 5 }}>FIRMA DEL ASESOR</Text>
            </View>
        </View>
    </View>
);

const Footer = () => (
    <View style={styles.footer}>
        {PIE_PAGINA.map((linea, i) => (
            <Text key={i}>{linea}</Text>
        ))}
    </View>
);

const AuthBox = ({ title }) => (
    <View style={styles.authBox}>
        <Text style={styles.authText}>{title}</Text>
    </View>
);

const NameDateRole = ({ labels }) => (
    <Row style={{ borderWidth: 0.5, borderColor: COLORS.border }}>
        {labels.map((label, i) => (
            <View
                key={label}
                style={{
                    flex: 1,
                    paddingVertical: 4,
                    paddingHorizontal: 4,
                    borderRightWidth: i < labels.length - 1 ? 0.5 : 0,
                    borderColor: COLORS.border
                }}
            >
                <Text style={{ fontFamily: 'Helvetica-BoldOblique', fontSize: 6 }}>{label}</Text>
            </View>
        ))}
    </Row>
);

const PageOne = ({ data }) => {
    const { cliente, referencia, credito, fecha } = data;

    return (
        <>
            <Header />

            <View style={styles.bannerRow}>
                <View style={styles.bannerWrap}>
                    <Text style={styles.banner}>INFORMACION GENERAL DEL CREDITO</Text>
                </View>
                <View style={styles.numbersCol}>
                    <View style={styles.numberRow}>
                        <Text style={styles.numberLabel}>N° Crédito</Text>
                        <Text style={styles.numberValue}>{data.numeroCredito || ' '}</Text>
                    </View>
                    <View style={styles.numberRow}>
                        <Text style={styles.numberLabel}>N° Solicitud</Text>
                        <Text style={styles.numberValue}>{data.numeroSolicitud || ' '}</Text>
                    </View>
                    <View style={styles.numberRow}>
                        <Text style={styles.numberLabel}>N° Liquidación</Text>
                        <Text style={styles.numberValue}>{data.numeroLiquidacion || ' '}</Text>
                    </View>
                </View>
            </View>

            <View style={[styles.box, { width: 260, marginTop: 3, paddingVertical: 3 }]}>
                <Row style={{ alignItems: 'flex-end' }}>
                    <Text style={styles.label}>Fecha de Solicitud:</Text>
                    <Field label="Día" value={fecha.dia} style={{ width: 58, marginBottom: 0 }} />
                    <Field label="Mes" value={fecha.mes} style={{ width: 62, marginBottom: 0 }} />
                    <Field label="Año" value={fecha.anio} style={{ width: 66, marginBottom: 0 }} />
                </Row>
            </View>

            <Text style={styles.sectionTitle}>1-   DATOS BÁSICOS DEL CLIENTE</Text>
            <View style={styles.greenBox}>
                <Field label="Nombres o Razón Social:" value={cliente.nombre} />
                <Row>
                    <Field label="Tipo De Documento:" value={cliente.tipoDocumento} style={{ width: 110, marginRight: 6 }} />
                    <Field label="Número:" value={cliente.identificacion} style={{ flex: 1, marginRight: 6 }} />
                    <Field label="De" value={cliente.lugarExpedicion} style={{ flex: 1 }} />
                </Row>
                <Row>
                    <Field label="Dirección:" value={cliente.direccion} style={{ flex: 1, marginRight: 6 }} />
                    <Field label="Ciudad:" value={cliente.ciudad} style={{ width: 200 }} />
                </Row>
                <Row>
                    <Field label="Email:" value={cliente.email} style={{ flex: 1, marginRight: 6 }} />
                    <Field label="Celular:" value={cliente.celular} style={{ width: 200 }} />
                </Row>
                <Field label="Entidad Pagadora:" value={cliente.entidadPagadora} style={{ marginBottom: 0 }} />
            </View>

            <Text style={styles.sectionTitle}>1.1 REFERENCIA PERSONAL OBLIGATORIA</Text>
            <View style={styles.box}>
                <Row>
                    <Field label="Nombres y Apellidos:" value={referencia.nombre} style={{ flex: 1.4, marginRight: 6 }} />
                    <Field label="Identificación:" value={referencia.identificacion} style={{ flex: 1, marginRight: 6 }} />
                    <Field label="Celular" value={referencia.celular} style={{ flex: 1 }} />
                </Row>
                <Row>
                    <Field label="Email:" value={referencia.email} style={{ flex: 1, marginRight: 6, marginBottom: 0 }} />
                    <Field label="Dirección:" value={referencia.direccion} style={{ flex: 1.2, marginRight: 6, marginBottom: 0 }} />
                    <Field label="Ciudad:" value={referencia.ciudad} style={{ flex: 0.8, marginBottom: 0 }} />
                </Row>
            </View>

            <Text style={styles.sectionTitle}>2-   INFORMACIÓN BÁSICA DEL CRÉDITO</Text>
            <View style={[styles.box, { paddingVertical: 5 }]}>
                <Row>
                    <Field label="Monto Transado: $" value={credito.montoTransado} style={{ flex: 1.5, marginRight: 5, marginBottom: 0 }} />
                    <Field label="N° Cuotas" value={credito.cuotas} style={{ width: 52, marginRight: 5, marginBottom: 0 }} />
                    <Field label="Línea Crédito" value={credito.linea} style={{ flex: 1.2, marginRight: 5, marginBottom: 0 }} />
                    <Field label="Sub-Línea" value={credito.subLinea} style={{ flex: 1.2, marginRight: 5, marginBottom: 0 }} />
                    <Field label="TASA %" value={credito.tasa} style={{ width: 52, marginBottom: 0 }} />
                </Row>
            </View>

            <Row style={{ marginTop: 4, alignItems: 'flex-start' }}>
                <View style={[styles.greenBox, { width: 224, marginRight: 6, paddingVertical: 6 }]}>
                    <Field label="Valor Cuotas: $" value={credito.valorCuota} style={{ marginBottom: 0 }} />
                </View>
                <View style={[styles.box, { flex: 1, marginRight: 6, paddingVertical: 6 }]}>
                    <Field label="Mes de Novedad:" value={credito.mesNovedad} style={{ marginBottom: 0 }} />
                </View>
                <AuthBox title={'Autorización\nGerencia'} />
            </Row>

            <Row style={{ marginTop: 2 }}>
                <Text style={[styles.sectionTitle, { width: 230 }]}>3-   LIQUIDACIÓN DEL MONTO A DESEMBOLSAR</Text>
                <Text style={styles.sectionTitle}>4-   FORMAS DE PAGOS</Text>
            </Row>

            <Row style={{ alignItems: 'flex-start' }}>
                <View style={[styles.box, { width: 230, marginRight: 6 }]}>
                    <Field label="MONTO TRANSADO:" value={credito.montoTransado} />
                    <Field label="VLR NOVACION" />
                    <Field label="VLR CARTERA EXT" />
                    <Field label="INTERES ANTICIPADO:" />
                    <Field label="TOTAL ANTICIPOS" />
                    <Field label="ESTUDIO DE CREDITO:" />
                    <Field label="OTROS PAGOS:" />
                    <Text style={[styles.label, { fontFamily: 'Helvetica-BoldOblique', marginTop: 2 }]}>
                        MONTO A DESEMBOLSAR:
                    </Text>
                    <View style={[styles.box, { marginTop: 2, paddingVertical: 5 }]}>
                        <Text style={styles.value}>$</Text>
                    </View>
                </View>

                <View style={{ flex: 1, marginRight: 6 }}>
                    <View style={[styles.greenBox, { marginBottom: 4 }]}>
                        <Text style={styles.boxTitle}>Efectivo</Text>
                        <Field label="Valor Efectivo:" style={{ marginBottom: 0 }} />
                    </View>
                    <View style={[styles.greenBox, { marginBottom: 4 }]}>
                        <Text style={styles.boxTitle}>Transferencia o Consignación</Text>
                        <Field label="Entidad Financiera:" />
                        <Field label="N° Cta Destino:" />
                        <Row>
                            <Field label="Tipo Cuenta Destino: AH" style={{ flex: 1, marginRight: 6 }} />
                            <Field label="CTE" style={{ width: 90 }} />
                        </Row>
                        <Field label="Valor Transferencia:" style={{ marginBottom: 0 }} />
                    </View>
                    <View style={styles.greenBox}>
                        <Text style={styles.boxTitle}>Cheque</Text>
                        <Field label="Entidad Origen:" />
                        <Row>
                            <Field label="Cta. Origen:" style={{ flex: 1, marginRight: 6 }} />
                            <Field label="Tipo: AH" style={{ width: 62, marginRight: 6 }} />
                            <Field label="CTE" style={{ width: 62 }} />
                        </Row>
                        <Field label="N° Cheque:" />
                        <Field label="Valor Cheque:" style={{ marginBottom: 0 }} />
                    </View>
                </View>

                <AuthBox title={'Autorización\nArchivo'} />
            </Row>

            <Row style={{ marginTop: 5, alignItems: 'flex-start' }}>
                <View style={[styles.box, { width: 230, marginRight: 6, paddingHorizontal: 5 }]}>
                    <Text style={[styles.boxTitle, { fontSize: 6 }]}>RESUMEN ANTICIPOS CREDITOS</Text>
                    <View style={styles.tableHeader}>
                        <Text style={[styles.tableCell, { width: 18, textAlign: 'center' }]}>#</Text>
                        <Text style={[styles.tableCell, { flex: 1, textAlign: 'center' }]}>Comprobante</Text>
                        <Text style={[styles.tableCell, { flex: 1, textAlign: 'center' }]}>Valor Anticipo</Text>
                        <Text style={[styles.tableCellLast, { flex: 1, textAlign: 'center' }]}>Concepto</Text>
                    </View>
                    {[...Array(FILAS_ANTICIPOS)].map((_, i) => (
                        <View key={i} style={styles.tableRow}>
                            <Text style={[styles.tableCell, { width: 18, minHeight: 9 }]}> </Text>
                            <Text style={[styles.tableCell, { flex: 1 }]}> </Text>
                            <Text style={[styles.tableCell, { flex: 1 }]}> </Text>
                            <Text style={[styles.tableCellLast, { flex: 1 }]}> </Text>
                        </View>
                    ))}
                </View>

                <View style={[styles.box, { flex: 1, height: 104 }]}>
                    <Text style={[styles.label, { fontFamily: 'Helvetica-BoldOblique' }]}>
                        Observación General del Crédito:
                    </Text>
                    <Text style={[styles.value, { marginTop: 3 }]}>{credito.observacion || ' '}</Text>
                </View>
            </Row>

            <Text style={[styles.sectionHeader, { marginTop: 6 }]}>
                5-   IDENTIFICACIÓN BENEFICIARIO FINAL (APLICA PARA PERSONA JURÍDICA)
            </Text>
            <Text style={[styles.legalText, { marginVertical: 2 }]}>{BENEFICIARIO_FINAL_INTRO}</Text>
            <View style={styles.tableHeader}>
                <Text style={[styles.tableCell, { width: 52 }]}>TIPO ID</Text>
                <Text style={[styles.tableCell, { width: 70 }]}>N° ID</Text>
                <Text style={[styles.tableCell, { flex: 1 }]}>NOMBRE COMPLETO</Text>
                <Text style={[styles.tableCell, { width: 138 }]}>
                    ¿POR SU CARGO O ACTIVIDAD, MANEJA RECURSOS PÚBLICOS?
                </Text>
                <Text style={[styles.tableCellLast, { width: 138 }]}>
                    ¿POR SU CARGO O ACTIVIDAD, EJERCE ALGÚN GRADO DE PODER PÚBLICO?
                </Text>
            </View>
            {[...Array(FILAS_BENEFICIARIO)].map((_, i) => (
                <View key={i} style={styles.tableRow}>
                    <Text style={[styles.tableCell, { width: 52, minHeight: 10 }]}> </Text>
                    <Text style={[styles.tableCell, { width: 70 }]}> </Text>
                    <Text style={[styles.tableCell, { flex: 1 }]}> </Text>
                    <Text style={[styles.tableCell, { width: 138 }]}>SI       ___         NO ___</Text>
                    <Text style={[styles.tableCellLast, { width: 138 }]}>SI       ___         NO ___</Text>
                </View>
            ))}
        </>
    );
};

const PageTwo = () => (
    <>
        <Header />

        <Text style={styles.sectionHeader}>
            6-   IDENTIFICACIÓN DE PERSONA EXPUESTA POLÍTICAMENTE - PEP E IDENTIFICACIÓN DE SUS RELACIONADOS
        </Text>
        <View style={styles.legalBox}>
            <Text style={styles.legalText}>{PEP_DEFINICION}</Text>
            <Text style={[styles.legalText, { marginTop: 4 }]}>{PEP_DECLARACION}</Text>
        </View>

        <Text style={styles.sectionHeader}>7-   APROBACIÓN DE PEP</Text>
        <View style={styles.legalBox}>
            <Text style={styles.legalText}>{PEP_APROBACION}</Text>
        </View>
        <NameDateRole labels={['Nombre:', 'Fecha:', 'Cargo:']} />

        <Text style={styles.sectionHeader}>8-   DECLARACIÒN DE ORIGEN-DESTINACIÓN DE FONDOS</Text>
        <View style={styles.legalBox}>
            {ORIGEN_FONDOS.map((parrafo, i) => (
                <Row key={i} style={{ marginBottom: 3 }}>
                    <Text style={[styles.legalText, { width: 14, textAlign: 'left' }]}>{`${i + 1}.`}</Text>
                    <Text style={[styles.legalText, { flex: 1 }]}>{parrafo}</Text>
                </Row>
            ))}
        </View>

        <Text style={[styles.sectionHeader, { fontFamily: 'Helvetica-BoldOblique' }]}>
            ESPACIO PARA USO EXCLUSIVO DE INVERSIONES OSVIEL S.A.S
        </Text>

        <Text style={styles.sectionHeader}>9-   APROBACIÓN DE PEP</Text>
        <View style={styles.legalBox}>
            <Text style={styles.legalText}>{PEP_APROBACION}</Text>
        </View>
        <NameDateRole labels={['Nombre:', 'Fecha:', 'Cargo:']} />

        <Text style={styles.sectionHeader}>10-   RESULTADOS DE LA CONSULTA EN LISTAS</Text>
        <View style={styles.legalBox}>
            <Text style={styles.legalText}>{RESULTADO_LISTAS}</Text>
        </View>

        <Text style={styles.sectionHeader}>11-   RESPONSABLE DEBIDA DILIGENCIA</Text>
        <NameDateRole labels={['Nombre:', 'Cargo:', 'Fecha:']} />

        <Text style={styles.sectionHeader}>CONDICIONES LEGALES</Text>
        <View style={{ marginTop: 3 }}>
            {CONDICIONES_LEGALES.map(({ titulo, texto }) => (
                <Text key={titulo} style={[styles.conditionsText, { marginBottom: 1.5 }]}>
                    <Text style={{ fontFamily: 'Helvetica-Bold' }}>{titulo}</Text>
                    {` ${texto}`}
                </Text>
            ))}
        </View>
        <Text style={[styles.conditionsText, { fontFamily: 'Helvetica-Bold', textAlign: 'center', marginTop: 4 }]}>
            {CONSTANCIA}
        </Text>

        <View style={styles.signatureRow}>
            <Text style={styles.signatureLine}>Firma y CC del Cliente y/o Beneficiario crédito</Text>
            <View style={styles.fingerprintBox}>
                <Text>Huella</Text>
            </View>
            <Text style={styles.signatureLine}>Firma y CC del Cliente y/o Beneficiario crédito</Text>
            <View style={styles.fingerprintBox}>
                <Text>Huella</Text>
            </View>
        </View>

        <Footer />
    </>
);

const SolicitudFormato = ({ data }) => {
    if (!data) return null;

    return (
        <Document title={`Solicitud de crédito ${data.numeroSolicitud || ''}`}>
            <Page size="A4" style={styles.page}>
                <PageOne data={data} />
            </Page>
            <Page size="A4" style={styles.page}>
                <PageTwo />
            </Page>
        </Document>
    );
};

export default SolicitudFormato;
